import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jourDeTunis, peutEmbaucher, type CandidatureDetail, type Embauche, type EmployeDetail } from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';

export interface PointDeVente {
  id: string;
  code: string;
  libelle: string;
  actif: boolean;
}

/**
 * Bascule en fiche employé (docs/ROADMAP.md point 21) : possible une fois les
 * justificatifs reçus. L'affectation à un point de vente se fait ici.
 */
export function Embaucher({ fiche }: { fiche: CandidatureDetail }) {
  const navigate = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const [points, setPoints] = useState<PointDeVente[]>([]);
  const [saisie, setSaisie] = useState<Embauche>({
    dateEntree: jourDeTunis(),
    poste: fiche.annonceIntitule,
    pointDeVenteId: '',
    matricule: '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!ouvert) return;
    appel<PointDeVente[]>('/points-de-vente')
      .then(setPoints)
      .catch((e: Error) => setErreur(e.message));
  }, [ouvert]);

  if (fiche.employe || !peutEmbaucher(fiche.statut)) return null;
  if (!ouvert) {
    return (
      <button type="button" className="bouton" onClick={() => setOuvert(true)}>
        Embaucher
      </button>
    );
  }

  async function valider(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      const employe = await appel<EmployeDetail>(`/candidatures/${fiche.id}/embaucher`, {
        method: 'POST',
        body: JSON.stringify({ ...saisie, matricule: saisie.matricule?.trim() || null }),
      });
      navigate(`/employes/${employe.id}`);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Embauche impossible');
      setEnvoi(false);
    }
  }

  return (
    <form
      className="formulaire-employe"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        void valider();
      }}
    >
      <h3>Embauche</h3>
      <label className="champ">
        Date d’entrée
        <input
          type="date"
          required
          value={saisie.dateEntree}
          onChange={(e) => setSaisie({ ...saisie, dateEntree: e.target.value })}
        />
      </label>
      <label className="champ">
        Poste
        <input
          type="text"
          required
          minLength={2}
          maxLength={120}
          value={saisie.poste}
          onChange={(e) => setSaisie({ ...saisie, poste: e.target.value })}
        />
      </label>
      <label className="champ">
        Point de vente
        <select
          required
          value={saisie.pointDeVenteId}
          onChange={(e) => setSaisie({ ...saisie, pointDeVenteId: e.target.value })}
        >
          <option value="">Choisir…</option>
          {points.map((point) => (
            <option key={point.id} value={point.id}>
              {point.libelle}
            </option>
          ))}
        </select>
      </label>
      <label className="champ">
        Matricule (facultatif)
        <input
          type="text"
          maxLength={30}
          value={saisie.matricule ?? ''}
          onChange={(e) => setSaisie({ ...saisie, matricule: e.target.value })}
        />
      </label>
      {erreur && <Erreur message={erreur} />}
      <div className="actions">
        <button type="button" className="lien" onClick={() => setOuvert(false)}>
          Annuler
        </button>
        <button type="submit" className="bouton" disabled={envoi}>
          Créer la fiche employé
        </button>
      </div>
    </form>
  );
}
